/**
 * Fantasy Console 0 - Confirm Dialog Component
 *
 * A modal dialog for confirming actions with customizable buttons.
 */

import { useEffect, useRef, useState } from 'react'
import styles from './ConfirmDialog.module.css'
import modalStyles from '../../styles/modal.module.css'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  confirmVariant?: 'primary' | 'danger'
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  // Focus cancel button when dialog opens
  useEffect(() => {
    if (isOpen) {
      cancelButtonRef.current?.focus()
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <div className={modalStyles.modalOverlay} onClick={onCancel}>
      <div
        className={`${modalStyles.modalDialog} ${styles.dialog}`}
        onClick={(e) => e.stopPropagation()}
        role='alertdialog'
        aria-modal='true'
        aria-labelledby='dialog-title'
        aria-describedby='dialog-message'
      >
        <h2
          id='dialog-title'
          className={`${modalStyles.modalTitle} ${styles.title}`}
        >
          {title}
        </h2>
        <p id='dialog-message' className={styles.message}>
          {message}
        </p>
        <div className={`${modalStyles.modalActions} ${styles.actions}`}>
          <button
            ref={cancelButtonRef}
            className={modalStyles.cancelButton}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            className={`${modalStyles.confirmButton} ${confirmVariant === 'danger' ? styles.danger : ''}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Hook to manage confirm dialog state
 */
export function useConfirmDialog() {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    confirmVariant?: 'primary' | 'danger'
    resolve?: (confirmed: boolean) => void
  }>({
    isOpen: false,
    title: '',
    message: '',
  })

  const showConfirmDialog = (options: {
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    confirmVariant?: 'primary' | 'danger'
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        ...options,
        resolve,
      })
    })
  }

  const handleConfirm = () => {
    dialogState.resolve?.(true)
    setDialogState((prev) => ({ ...prev, isOpen: false }))
  }

  const handleCancel = () => {
    dialogState.resolve?.(false)
    setDialogState((prev) => ({ ...prev, isOpen: false }))
  }

  const confirmDialog = (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      confirmLabel={dialogState.confirmLabel}
      cancelLabel={dialogState.cancelLabel}
      confirmVariant={dialogState.confirmVariant}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  )

  return {
    showConfirmDialog,
    confirmDialog,
  }
}
